/* eslint-disable no-nested-ternary */
import { Dispatch, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { Documents } from 'components/ApplicationDetails/AdvisorySection/AdvisorySection.types';
import { ApplicationStatus, DocStatus, DocTypes } from 'components/DocBox/DocBox.types';
import { ApplicationTypesEnum, CaseInvestmentPlan } from 'components/ApplicationDetails/Applications/Applications.types';
import { STATUS } from 'constants/constants';
import { dateFormatZurich, formatDate, isDateExpired } from 'utils/datetime';
import { ApplicationDetailsActionTypes } from 'datasource/useApplicationDetails/ApplicationDetailsManager.types';

type UseApplicationsListDerivationParams = {
    dispatch: Dispatch<any>;
    investmentAppsData: any[];
    applicationsDocumentsData: any;
    casesByContainerId: any;
    isAdvisoryCase: boolean;
    applicationsQuantity: number;
};

/**
 * Maps raw investment applications + their documents + related cases into the normalized
 * `applicationsData` shape, groups them by type, and pushes the result into the reducer.
 * Extracted verbatim from ApplicationDetailsManager (dep array preserved).
 */
export const useApplicationsListDerivation = ({
    dispatch,
    investmentAppsData,
    applicationsDocumentsData,
    casesByContainerId,
    isAdvisoryCase,
    applicationsQuantity,
}: UseApplicationsListDerivationParams) => {
    const { i18n: { language }, t } = useTranslation();

    useEffect(() => {
        const applications = investmentAppsData?.map((application) => {
            let docsPerApplicationId = applicationsDocumentsData?.filter((doc) => doc?.investmentApplicationId === application?.investmentApplicationId)?.map((doc) => ({
                status: doc?.signStatus === STATUS.submitted ? DocStatus.ReadyToSubmit : DocStatus.ReadyToSign,
                documentName: doc?.metadataName || doc?.documentName,
                documentId: doc?.id,
                applicationId: application?.investmentApplicationId,
                type: DocTypes.APPLICATION,
                isSent: doc?.isSent,
                signStatus: doc?.signStatus,
                documentReferenceId: doc?.documentReferenceId,
                externalId: doc?.externalId,
                isEsigned: doc?.isEsigned,
            }));

            docsPerApplicationId = docsPerApplicationId?.length > 0 ? docsPerApplicationId : [{
                status: DocStatus.ReadyToGenerate,
                type: DocTypes.GENERATE,
                applicationId: application?.investmentApplicationId,
            }];

            if ([ApplicationStatus.canceled, ApplicationStatus.completed, ApplicationStatus.revoked].includes(application?.status)) {
                docsPerApplicationId = docsPerApplicationId.reduce((akku: Documents, doc) => {
                    if (doc.type !== DocTypes.GENERATE
                        && doc.status !== DocStatus.ReadyToSign
                    ) akku.push(doc);

                    return akku;
                }, []);
            }

            let type = ApplicationTypesEnum.NEW;
            const changeInvestmentAmount = application?.investmentDescription?.changeInvestmentAmount;
            const isFollowUpInvestorProfile = changeInvestmentAmount?.isFollowUpInvestorProfile;

            if (application?.changePlan) {
                type = ApplicationTypesEnum.CHANGE;

                if (changeInvestmentAmount) {
                    if (isFollowUpInvestorProfile) {
                        type = ApplicationTypesEnum.UPDATE;
                    } else {
                        type = changeInvestmentAmount?.isSell ? ApplicationTypesEnum.SELL : ApplicationTypesEnum.BUY;
                    }
                }
            }

            const isNewEP = application?.isWithdrawalPlanChange && !application?.portfolioInfo?.hasWithdrawalPlan && !changeInvestmentAmount;
            const isChangeEP = application?.isWithdrawalPlanChange && application?.portfolioInfo?.hasWithdrawalPlan && !changeInvestmentAmount;

            if (isNewEP) {
                type = ApplicationTypesEnum.NEW_EP;
            }
            if (isChangeEP) {
                type = ApplicationTypesEnum.CHANGE_EP;
            }

            const caseInvestmentPlan: CaseInvestmentPlan | undefined = application?.investmentDescription?.changeInvestmentAmount ? {
                isCaseSource: application.investmentDescription.changeInvestmentAmount.isCaseSource,
                isSell: application.investmentDescription.changeInvestmentAmount.isSell,
                amount: application.investmentDescription.changeInvestmentAmount.value,
            } : undefined;

            const caseByInvestmentId = casesByContainerId?.data?.cases?.find(cse => cse?.investmentApplicationId === application?.investmentApplicationId);

            return {
                id: application?.investmentApplicationId,
                portfolioId: application?.portfolioInfo?.id,
                typeKey: type,
                type: t(type),
                productExternalId: application?.investmentDescription?.productExternalId,
                depotNumber: application?.portfolioInfo?.name,
                currentStrategy: application?.portfolioInfo?.strategy,
                investedAmount: application?.plannedInvestmentAmount ?? '-',
                amountToBeSold: changeInvestmentAmount?.value,
                purchaseAmount: changeInvestmentAmount?.value,
                productName: application?.strategy ?? '-',
                productNameTranslated: application?.productName ?? '-',
                applicationId: application?.investmentApplicationId ?? '-',
                createdDate: application?.createdDate ? formatDate(application?.createdDate, dateFormatZurich) : '-',
                validTill: application?.validTo ? formatDate(application?.validTo, dateFormatZurich) : '-',
                isApplicationExpired: isDateExpired(application?.validTo),
                advisor: application?.advisor ?? '-',
                createdBy: application?.createdBy ?? '-',
                investmentAmount: application?.plannedInvestmentAmount ?? '-',
                submitted: caseInvestmentPlan?.isSell ? (caseByInvestmentId?.submitted ? formatDate(caseByInvestmentId?.submitted, dateFormatZurich) : '-') : (application?.submittedDate ? formatDate(application?.submittedDate, dateFormatZurich) : '-'),
                submittedBy: caseInvestmentPlan?.isSell ? caseByInvestmentId?.submittedBy : application?.submittedBy,
                documents: docsPerApplicationId,
                isSentCase: docsPerApplicationId?.some((item) => (typeof item.isSent === 'boolean')),
                canBeDeleted: applicationsQuantity !== 1 && !isAdvisoryCase && ![ApplicationStatus.canceled, ApplicationStatus.revoked, ApplicationStatus.submitted, ApplicationStatus.completed].includes(application?.status),
                status: application?.status,
                rubrik: application?.investmentDescription?.productConfiguration?.rubrik,
                isSubmissionLocked: application?.isSubmissionLocked || false,
                caseStatus: caseByInvestmentId?.status,
                caseId: caseByInvestmentId?.id,
                caseOrderTypeId: caseByInvestmentId?.orderType,
                caseInvestmentPlan,
                isFollowUpInvestorProfile,
                isEsigned: docsPerApplicationId?.some((item) => !item.isEsigned),
                isSubmissionReviewPending: application?.investmentDescription?.isSubmissionReviewPending,
            };
        });

        const newApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.NEW);
        const changeApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.CHANGE);
        const sellApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.SELL);
        const buyApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.BUY);
        const newEPApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.NEW_EP);
        const changeEPApps = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.CHANGE_EP);
        const updateAPP = applications?.filter(({ typeKey }) => typeKey === ApplicationTypesEnum.UPDATE);

        dispatch({
            type: ApplicationDetailsActionTypes.SET_APPLICATIONS,
            payload: newApps.concat(changeApps, sellApps, buyApps, newEPApps, changeEPApps, updateAPP),
        });
    }, [investmentAppsData, applicationsDocumentsData, isAdvisoryCase, applicationsQuantity, language]);
};
